

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCouponModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        trip_id: '',
        departure_date_id: '',
        discount_type: 'Fixed',
        currency: 'GBP',
        activation_date: '',
        expiration_date: '',
        available_count: 0,
        used_count: 0,
        option_id: '',
        value: '',
        customer_id: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [trips, setTrips] = useState([]);
    const [departureDates, setDepartureDates] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [loadingDepartureDates, setLoadingDepartureDates] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchTrips();
            fetchCustomers();
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.trip_id) {
            fetchDepartureDates(formData.trip_id);
            fetchTripOptions(formData.trip_id);
        }
    }, [formData.trip_id]);

    const fetchTrips = async () => {
        setLoadingTrips(true);
        try {
            const response = await axiosInstance.get('/trips/');
            setTrips(response.data || []);
        } catch (error) {
            console.error('Error fetching trips:', error);
        } finally {
            setLoadingTrips(false);
        }
    };

    const fetchDepartureDates = async (tripId) => {
        setLoadingDepartureDates(true);
        try {
            const response = await axiosInstance.get(`/departure-dates/?trip_id=${tripId}`);
            setDepartureDates(response.data || []);
        } catch (error) {
            console.error('Error fetching departure dates:', error);
        } finally {
            setLoadingDepartureDates(false);
        }
    };

    const fetchTripOptions = async (tripId) => {
        setLoadingOptions(true);
        try {
            const response = await axiosInstance.get(`/trip-options/?trip_id=${tripId}`);
            setTripOptions(response.data || []);
        } catch (error) {
            console.error('Error fetching trip options:', error);
        } finally {
            setLoadingOptions(false);
        }
    };

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Coupon Name is required';
        if (!formData.code) errors.code = 'Coupon Code is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.departure_date_id) errors.departure_date_id = 'Departure Date is required';
        if (!formData.value) errors.value = 'Value is required';
        if (!formData.available_count && formData.available_count !== 0) errors.available_count = 'Available Count is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                name: formData.name,
                code: formData.code,
                trip_id: parseInt(formData.trip_id),
                departure_date_id: parseInt(formData.departure_date_id),
                discount_type: formData.discount_type,
                currency: formData.currency,
                activation_date: formData.activation_date || null,
                expiration_date: formData.expiration_date || null,
                available_count: parseInt(formData.available_count),
                used_count: parseInt(formData.used_count || 0),
                option_id: formData.option_id ? parseInt(formData.option_id) : null,
                value: parseFloat(formData.value),
                customer_id: formData.customer_id ? parseInt(formData.customer_id) : null
            };

            await onSave(payload);
            setFormData({
                name: '',
                code: '',
                trip_id: '',
                departure_date_id: '',
                discount_type: 'Fixed',
                currency: 'GBP',
                activation_date: '',
                expiration_date: '',
                available_count: 0,
                used_count: 0,
                option_id: '',
                value: '',
                customer_id: ''
            });
            onClose();
        } catch (error) {
            console.error('Error saving Coupon:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Coupon',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Coupon</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Coupon Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Coupon Code"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        error={!!formErrors.code}
                                        helperText={formErrors.code}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_id} required>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                            disabled={loadingTrips}
                                        >
                                            <MenuItem value=""><em>Select a Trip</em></MenuItem>
                                            {trips.map(trip => (
                                                <MenuItem key={trip.id} value={trip.id}>{trip.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.trip_id && <MDTypography variant="caption" color="error">{formErrors.trip_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.departure_date_id} required>
                                        <InputLabel>Departure Date</InputLabel>
                                        <Select
                                            name="departure_date_id"
                                            value={formData.departure_date_id}
                                            onChange={handleInputChange}
                                            label="Departure Date"

                                            disabled={loadingDepartureDates || !formData.trip_id}
                                        >
                                            <MenuItem value=""><em>Select a Departure Date</em></MenuItem>
                                            {departureDates.map(dd => (
                                                <MenuItem key={dd.id} value={dd.id}>{dd.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.departure_date_id && <MDTypography variant="caption" color="error">{formErrors.departure_date_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Discount Type</InputLabel>
                                        <Select
                                            name="discount_type"
                                            value={formData.discount_type}
                                            onChange={handleInputChange}
                                            label="Discount Type"

                                        >
                                            <MenuItem value="Fixed">Fixed</MenuItem>
                                            <MenuItem value="Percent">Percent</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            label="Currency"

                                        >
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="AUD">AUD</MenuItem>
                                            <MenuItem value="EUR">EUR</MenuItem>
                                            <MenuItem value="USD">USD</MenuItem>
                                            <MenuItem value="INR">INR</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Value"
                                        name="value"
                                        type="number"
                                        value={formData.value}
                                        onChange={handleInputChange}
                                        error={!!formErrors.value}
                                        helperText={formErrors.value}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available Count"
                                        name="available_count"
                                        type="number"
                                        value={formData.available_count}
                                        onChange={handleInputChange}
                                        error={!!formErrors.available_count}
                                        helperText={formErrors.available_count}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Used Count"
                                        name="used_count"
                                        type="number"
                                        value={formData.used_count}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Trip Option</InputLabel>
                                        <Select
                                            name="option_id"
                                            value={formData.option_id}
                                            onChange={handleInputChange}
                                            label="Trip Option"

                                            disabled={loadingOptions || !formData.trip_id}
                                        >
                                            <MenuItem value=""><em>Select a Trip Option</em></MenuItem>
                                            {tripOptions.map(opt => (
                                                <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Activation Date"
                                        name="activation_date"
                                        type="date"
                                        value={formData.activation_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expiration Date"
                                        name="expiration_date"
                                        type="date"
                                        value={formData.expiration_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Customer</InputLabel>
                                        <Select
                                            name="customer_id"
                                            value={formData.customer_id}
                                            onChange={handleInputChange}
                                            label="Customer"

                                            disabled={loadingCustomers}
                                        >
                                            <MenuItem value=""><em>Select a Customer</em></MenuItem>
                                            {customers.map(cust => (
                                                <MenuItem key={cust.id} value={cust.id}>{cust.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Coupon'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewCouponModal;

